import React from 'react';
import { Search } from 'lucide-react';

/** Lucide stroke width used everywhere in admin UI */
export const ADMIN_ICON_STROKE = 1.75;

/** Default page size for admin list tables */
export const DEFAULT_ADMIN_PAGE_SIZE = 10;

/**
 * @template T
 * @param {T[]} items
 * @param {number} page 1-based
 * @param {number} pageSize
 * @returns {{ pageItems: T[]; total: number; page: number; pageSize: number; totalPages: number }}
 */
export function paginateArray(items, page, pageSize) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const p = Math.min(Math.max(1, page), totalPages);
  const start = (p - 1) * pageSize;
  return {
    pageItems: items.slice(start, start + pageSize),
    total,
    page: p,
    pageSize,
    totalPages
  };
}

/**
 * @param {object} props
 * @param {string} props.placeholder
 * @param {string} props.value
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.onChange
 * @param {React.ReactNode} [props.extra] — e.g. filter dropdowns
 */
export function AdminSearchToolbar({ placeholder, value, onChange, extra }) {
  return (
    <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-4 md:p-5 mb-6">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <AdminToolbarIcon>
            <Search className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} />
          </AdminToolbarIcon>
          <input
            type="search"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className="w-full min-w-0 px-4 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
            autoComplete="off"
          />
        </div>
        {extra ? <div className="flex flex-wrap items-center gap-2 sm:justify-end">{extra}</div> : null}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {number} props.page — 1-based
 * @param {number} props.pageSize
 * @param {number} props.total
 * @param {(page: number) => void} props.onPageChange
 */
export function AdminPaginationBar({ page, pageSize, total, onPageChange, className = '' }) {
  if (total === 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 md:px-6 py-4 border-t border-[#E5E3DF] bg-[#FAF7F2] ${className}`}
    >
      <p className="text-[13px] text-[#78736A] tabular-nums">
        Showing {from}–{to} of {total}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          className="px-4 py-2 border border-[#E5E3DF] rounded-full text-sm font-medium text-black hover:bg-white bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        <span className="text-[13px] text-[#78736A] tabular-nums px-1">
          Page {safePage} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= totalPages}
          className="px-4 py-2 border border-[#E5E3DF] rounded-full text-sm font-medium text-black hover:bg-white bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export const adminCardClass =
  'bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF]';

/**
 * Coral icon on cream — same as Dashboard stat tiles.
 */
export function AdminIconBox({ children, className = '' }) {
  return (
    <div
      className={`p-2.5 rounded-[12px] bg-[#FAF7F2] text-[#FF5E4C] flex items-center justify-center shrink-0 ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * Black icon on cream — matches Dashboard “All sections” tiles.
 */
export function AdminIconBoxNeutral({ children, className = '' }) {
  return (
    <div
      className={`p-2.5 rounded-[12px] bg-[#FAF7F2] text-black flex items-center justify-center shrink-0 ${className}`}
    >
      {children}
    </div>
  );
}

/** Small icon for filters / toolbar (Search, Filter) */
export function AdminToolbarIcon({ children }) {
  return (
    <div className="p-2 rounded-[10px] bg-[#FAF7F2] text-[#FF5E4C] flex items-center justify-center shrink-0">
      {children}
    </div>
  );
}

export function AdminPageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-[20px] md:text-[22px] font-medium text-black tracking-[-0.02em]">{title}</h1>
        {subtitle ? <p className="text-[14px] text-[#78736A] mt-1">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

export function AdminSectionTitle({ children, className = '' }) {
  return <h2 className={`text-[16px] font-medium text-black ${className}`}>{children}</h2>;
}

/**
 * Single stat tile — matches Dashboard top metrics exactly (coral icon box).
 */
export function AdminStatCard({ label, value, icon: Icon, loading }) {
  return (
    <div className={`${adminCardClass} p-5 md:p-6`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[13px] font-medium text-[#78736A]">{label}</p>
          <p className="text-[22px] md:text-[24px] font-medium text-black mt-1 tabular-nums">
            {loading ? '…' : value}
          </p>
        </div>
        <AdminIconBox>
          <Icon className="w-6 h-6" strokeWidth={ADMIN_ICON_STROKE} />
        </AdminIconBox>
      </div>
    </div>
  );
}

/**
 * Responsive grid of stat cards — same layout as Dashboard.
 */
export function AdminStatGrid({ items, loading, className = '' }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 ${className}`}>
      {items.map((item) => (
        <AdminStatCard
          key={item.label}
          label={item.label}
          value={item.value}
          icon={item.icon}
          loading={loading}
        />
      ))}
    </div>
  );
}

/** Icon-only row actions — same cream + coral language as nav icons */
export function AdminTableIconButton({ children, className = '', title, ...rest }) {
  return (
    <button
      type="button"
      title={title}
      className={`p-2 rounded-[10px] bg-[#FAF7F2] text-[#FF5E4C] hover:opacity-90 transition-opacity ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function AdminTableIconButtonDanger({ children, className = '', title, ...rest }) {
  return (
    <button
      type="button"
      title={title}
      className={`p-2 rounded-[10px] bg-[#FAF7F2] text-[#DC2626] hover:opacity-90 transition-opacity ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
